import { useState } from 'react';
import { Button } from '@cropler/ui-kit';

import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import styles from './stylesChartsCustom.module.scss';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

import { CustomCharts } from '../../constants/keys';
import { GetChartTitle } from './lib/index.jsx';

const ToSortableItems = (pairs) =>
  pairs.map((pair, index) => ({
    id: `${pair.join('-')}-${index}`,
    pair,
  }));

const SortableChartItem = ({ item, chartsMap, onDelete, t }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const title1 = GetChartTitle(item.pair[0], chartsMap);
  const title2 = GetChartTitle(item.pair[1], chartsMap);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.chartsCustomSettingsItem} ${isDragging ? styles.chartsCustomSettingsItemDragging : ''}`}
      {...attributes}
      {...listeners}
    >
      <CroplerIcon name="dnd" />
      <div className={styles.chartsCustomSettingsItemLabel}>
        {title1} — {title2}
      </div>
      <Button
        type="text-danger"
        icon={<CroplerIcon name="remove" />}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        title={t('actions.delete')}
      />
    </div>
  );
};

const ChartsCustomSettings = ({ chartsMap, metricsMap, saveChart, createChart, t }) => {
  const [customCharts, setCustomCharts] = useState(() =>
    ToSortableItems(JSON.parse(localStorage.getItem(CustomCharts) || '[]')),
  );
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDeleteChart = (id) => {
    setCustomCharts((prev) => prev.filter((item) => item.id !== id));
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setCustomCharts((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);

      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const onSave = () => {
    localStorage.setItem(CustomCharts, JSON.stringify(customCharts.map((item) => item.pair)));
    saveChart();
  };

  const onCreateChart = () => {
    createChart();
  };

  return (
    <div className={styles.chartsCustomFilter}>
      <div className={styles.chartsCustomFilterList}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={customCharts.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <div className={styles.chartsCustomSettingsList}>
              {customCharts.map((item) => {
                if (!metricsMap.includes(item.pair[0]) || !metricsMap.includes(item.pair[1])) return;
                return (
                  <SortableChartItem key={item.id} item={item} chartsMap={chartsMap} onDelete={onDeleteChart} t={t} />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>
      <div className={styles.chartsCustomFilterNav}>
        <Button type="secondary" size="large" onClick={onCreateChart}>
          <CroplerIcon name="add" />
          {t('charts.custom.newChart')}
        </Button>
        <Button type="primary" size="large" onClick={onSave}>
          {t('actions.saveChanges')}
        </Button>
      </div>
    </div>
  );
};

export default ChartsCustomSettings;
